import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  FileText, CheckCircle, Clock, PenTool, Download,
  Loader2, AlertCircle, Building, PoundSterling, User
} from "lucide-react";
import SignatureCanvas from "react-signature-canvas";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SellerContractPage() {
  const token = localStorage.getItem("portal_token");
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [signing, setSigning] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  
  // Form state
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [declarations, setDeclarations] = useState({
    1: false,
    2: false,
    3: false,
    4: false
  });
  
  const sigCanvas = useRef(null);

  useEffect(() => {
    fetchContract();
  }, []);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/api/contracts/seller/my-contract`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContract(response.data.contract);
      if (response.data.contract) {
        setPhone(response.data.contract.seller_phone || "");
        setEmail(response.data.contract.seller_email || "");
        setMobile(response.data.contract.seller_mobile || "");
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeclarationChange = (num) => {
    setDeclarations(prev => ({ ...prev, [num]: !prev[num] }));
  };

  const allDeclarationsAgreed = () => {
    return declarations[1] && declarations[2] && declarations[3] && declarations[4];
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleSign = async () => {
    if (!allDeclarationsAgreed()) {
      toast.error("Please agree to all declarations before signing");
      return;
    }
    
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      toast.error("Please provide your signature");
      return;
    }
    
    setSigning(true);
    try {
      const signatureData = sigCanvas.current.toDataURL("image/png");
      
      await axios.post(`${API}/api/contracts/seller/sign`, {
        signature_data: signatureData,
        declarations_agreed: [1, 2, 3, 4],
        contact_phone: phone,
        contact_email: email,
        contact_mobile: mobile
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Contract signed successfully! Waiting for Goodacres to countersign.");
      fetchContract();
      setShowSignature(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign contract");
    } finally {
      setSigning(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending_seller_signature":
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Awaiting Your Signature</Badge>;
      case "pending_staff_signature":
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" /> Awaiting Goodacres Signature</Badge>;
      case "completed":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Completed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Contract Available</h3>
            <p className="text-slate-500">Your contract is being prepared. You'll receive an email when it's ready to sign.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Property Instruction Contract</h1>
          <p className="text-slate-500">Review and sign your property sale agreement</p>
        </div>
        {getStatusBadge(contract.status)}
      </div>

      {/* Property Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="w-5 h-5" />
            Property Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">Property Address</Label>
              <p className="font-medium">{contract.property_address}</p>
            </div>
            <div>
              <Label className="text-slate-500">Postcode</Label>
              <p className="font-medium">{contract.property_postcode}</p>
            </div>
            <div>
              <Label className="text-slate-500">Asking Price</Label>
              <p className="font-medium text-lg">£{contract.asking_price?.toLocaleString()}</p>
            </div>
            <div>
              <Label className="text-slate-500">Agency Type</Label>
              <p className="font-medium">{contract.agency_type === "sole" ? "Sole Agency" : "Multiple Agency"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commission Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PoundSterling className="w-5 h-5" />
            Commission & Fees
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-600">Commission Type</span>
              <span className="font-medium">
                {contract.commission_type === "percentage" 
                  ? `${contract.commission_percentage}% of sale price`
                  : `Fixed fee: £${contract.commission_fixed?.toLocaleString()}`
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Base Fee (on asking price)</span>
              <span className="font-medium">£{contract.base_fee?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">VAT ({contract.vat_rate}%)</span>
              <span className="font-medium">£{contract.vat_amount?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between border-t pt-3">
              <span className="font-semibold">Total Fee (inc. VAT)</span>
              <span className="font-bold text-lg">£{contract.total_fee?.toLocaleString()}</span>
            </div>
            {contract.minimum_fee && (
              <div className="flex justify-between text-sm text-slate-500">
                <span>Minimum Fee (if applicable)</span>
                <span>£{contract.minimum_fee?.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Sole Agency Period</span>
              <span className="font-medium">{contract.sole_agency_period_weeks} weeks</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Details (if not signed) */}
      {contract.status === "pending_seller_signature" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Your Contact Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="phone">Telephone</Label>
                <Input 
                  id="phone" 
                  value={phone} 
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="01234 567890"
                />
              </div>
              <div>
                <Label htmlFor="mobile">Mobile</Label>
                <Input 
                  id="mobile" 
                  value={mobile} 
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="07xxx xxxxxx"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email"
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Declarations (if not signed) */}
      {contract.status === "pending_seller_signature" && (
        <Card>
          <CardHeader>
            <CardTitle>Declarations</CardTitle>
            <p className="text-sm text-slate-500">Please read and agree to all declarations</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox 
                id="dec1" 
                checked={declarations[1]}
                onCheckedChange={() => handleDeclarationChange(1)}
              />
              <label htmlFor="dec1" className="text-sm cursor-pointer">
                <strong>1.</strong> I confirm that I will be personally liable for the payment of the fee due under this agreement.
              </label>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox 
                id="dec2" 
                checked={declarations[2]}
                onCheckedChange={() => handleDeclarationChange(2)}
              />
              <label htmlFor="dec2" className="text-sm cursor-pointer">
                <strong>2.</strong> I have read, understood and accept the terms and conditions of this agreement and acknowledge receipt of a copy of the agreement.
              </label>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox 
                id="dec3" 
                checked={declarations[3]}
                onCheckedChange={() => handleDeclarationChange(3)}
              />
              <label htmlFor="dec3" className="text-sm cursor-pointer">
                <strong>3.</strong> I confirm that I am either the owner of the EPC or that I am exempt from providing one.
              </label>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox 
                id="dec4" 
                checked={declarations[4]}
                onCheckedChange={() => handleDeclarationChange(4)}
              />
              <label htmlFor="dec4" className="text-sm cursor-pointer">
                <strong>4.</strong> I consent to the release of my file to the Ombudsman in the event of a dispute.
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature Section */}
      {contract.status === "pending_seller_signature" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Your Signature
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!showSignature ? (
              <Button 
                onClick={() => setShowSignature(true)}
                disabled={!allDeclarationsAgreed()}
                className="w-full"
                data-testid="open-signature-btn"
              >
                <PenTool className="w-4 h-4 mr-2" />
                {allDeclarationsAgreed() ? "Sign Contract" : "Agree to all declarations to sign"}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      width: 600,
                      height: 200,
                      className: "signature-canvas w-full"
                    }}
                    backgroundColor="white"
                  />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={clearSignature}>
                    Clear
                  </Button>
                  <Button variant="outline" onClick={() => setShowSignature(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSign} 
                    disabled={signing}
                    className="flex-1"
                    data-testid="submit-signature-btn"
                  >
                    {signing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Submit Signature
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Signed Status */}
      {contract.status === "pending_staff_signature" && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Contract Signed - Awaiting Countersignature</h3>
                <p className="text-blue-700 text-sm">
                  You signed on {new Date(contract.seller_signed_at).toLocaleDateString()}. 
                  Goodacres will review and countersign shortly.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed Status */}
      {contract.status === "completed" && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-900">Contract Complete!</h3>
                  <p className="text-green-700 text-sm">
                    Signed by you on {new Date(contract.seller_signed_at).toLocaleDateString()} and 
                    countersigned by {contract.staff_signer_name} on {new Date(contract.staff_signed_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Button variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
